import "./CustomerBalanceChecker.css";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/* ─── Helpers ─── */
const fmt = (n) => Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-NG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* ─── Icons ─── */
const IconWallet = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#2563EB' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2' />
    <path d='M16 12h5v4h-5a2 2 0 0 1 0-4Z' />
  </svg>
);
const IconSearch = () => (
  <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <circle cx='11' cy='11' r='8' />
    <path d='m21 21-4.35-4.35' />
  </svg>
);
const IconCheck = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#16a34a' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <polyline points='20 6 9 17 4 12' />
  </svg>
);
const IconPlus = () => (
  <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <line x1='12' y1='5' x2='12' y2='19' />
    <line x1='5' y1='12' x2='19' y2='12' />
  </svg>
);
const IconTrash = () => (
  <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <polyline points='3 6 5 6 21 6' />
    <path d='M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' />
    <path d='M10 11v6' />
    <path d='M14 11v6' />
    <path d='M9 6V4h6v2' />
  </svg>
);
const IconX = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
    <line x1='18' y1='6' x2='6' y2='18' />
    <line x1='6' y1='6' x2='18' y2='18' />
  </svg>
);
const IconInfo = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <circle cx='12' cy='12' r='10' />
    <line x1='12' y1='8' x2='12' y2='12' />
    <line x1='12' y1='16' x2='12.01' y2='16' />
  </svg>
);
const IconWarning = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z' />
    <line x1='12' y1='9' x2='12' y2='13' />
    <line x1='12' y1='17' x2='12.01' y2='17' />
  </svg>
);

/* ══════════════════════════════════════
   CONFIRM DIALOG  (Portal-rendered)
   type: "add" | "clear"
══════════════════════════════════════ */
function ConfirmDialog({ type, balance, currency, customerName, onConfirm, onCancel }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const isAdd = type === "add";

  const config = {
    add: {
      iconBg: "#dbeafe",
      iconColor: "#2563EB",
      icon: <IconInfo />,
      title: "Add Balance to Transaction",
      confirmCls: "cbc-confirm-btn-blue",
      confirmLabel: `Yes, Add ${currency} ${fmt(balance)}`,
      bullets: [
        { icon: "✓", color: "#16a34a", text: `${currency} ${fmt(balance)} will be added to the current transaction total.` },
        { icon: "✓", color: "#16a34a", text: `All outstanding loan balance for ${customerName} will be fully cleared.` },
        { icon: "✓", color: "#16a34a", text: "This cleared amount will appear in today's store transactions as a cleared loan." },
      ],
    },
    clear: {
      iconBg: "#fef3c7",
      iconColor: "#d97706",
      icon: <IconWarning />,
      title: "Clear Customer Balance",
      confirmCls: "cbc-confirm-btn-amber",
      confirmLabel: "Yes, Clear Balance",
      bullets: [
        { icon: "✓", color: "#16a34a", text: `All outstanding loan balance for ${customerName} will be cleared.` },
        { icon: "✗", color: "#dc2626", text: "This balance will NOT be added to the current transaction." },
        { icon: "✓", color: "#16a34a", text: "The cleared amount will appear in today's store transactions as a cleared loan." },
      ],
    },
  };

  const c = config[type];

  return createPortal(
    <div
      className='cbc-overlay cbc-confirm-overlay'
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onCancel();
      }}
    >
      <div className='cbc-confirm-sheet' role='alertdialog' aria-modal='true'>
        {/* Icon */}
        <div className='cbc-confirm-icon-wrap' style={{ background: c.iconBg, color: c.iconColor }}>
          {c.icon}
        </div>

        {/* Title */}
        <div className='cbc-confirm-title'>{c.title}</div>

        {/* Amount chip */}
        <div className='cbc-confirm-amount'>
          {currency} {fmt(balance)}
        </div>

        {/* Bullet list */}
        <ul className='cbc-confirm-bullets'>
          {c.bullets.map((b, i) => (
            <li key={i} className='cbc-confirm-bullet'>
              <span className='cbc-bullet-icon' style={{ color: b.color }}>
                {b.icon}
              </span>
              <span className='cbc-bullet-text'>{b.text}</span>
            </li>
          ))}
        </ul>

        {/* Actions */}
        <div className='cbc-confirm-actions'>
          <button className='cbc-confirm-btn cbc-confirm-btn-cancel' onClick={onCancel}>
            Cancel
          </button>
          <button className={`cbc-confirm-btn ${c.confirmCls}`} onClick={onConfirm}>
            {c.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ══════════════════════════════════════
   TRANSACTION DETAIL MODAL
══════════════════════════════════════ */
function TransactionModal({ transactions, balance, currency, customerName, onAddToTransaction, onClearBalance, onClose }) {
  const overlayRef = useRef(null);
  const [confirm, setConfirm] = useState(null); // null | "add" | "clear"

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && !confirm) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, confirm]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const totalPaid = transactions.reduce((s, tx) => s + (tx.operationType?.intialPayment || 0), 0);
  const txCount = transactions.length;

  return createPortal(
    <>
      <div
        className='cbc-overlay'
        ref={overlayRef}
        onClick={(e) => {
          if (e.target === overlayRef.current && !confirm) onClose();
        }}
      >
        <div className='cbc-sheet' role='dialog' aria-modal='true'>
          <div className='cbc-handle' />

          <div className='cbc-modal-hdr'>
            <div>
              <div className='cbc-modal-title'>Outstanding Balance</div>
              <div className='cbc-modal-sub'>
                {customerName} · {txCount} loan transaction{txCount !== 1 ? "s" : ""}
              </div>
            </div>
            <button className='cbc-close-btn' onClick={onClose} aria-label='Close'>
              <IconX />
            </button>
          </div>

          <div className='cbc-modal-summary'>
            <div className='cbc-ms-cell'>
              <div className='cbc-ms-label'>Total Owed</div>
              <div className='cbc-ms-val cbc-ms-red'>
                {currency} {fmt(balance)}
              </div>
            </div>
            <div className='cbc-ms-cell'>
              <div className='cbc-ms-label'>Initial Paid</div>
              <div className='cbc-ms-val'>
                {currency} {fmt(totalPaid)}
              </div>
            </div>
            <div className='cbc-ms-cell'>
              <div className='cbc-ms-label'>Transactions</div>
              <div className='cbc-ms-val'>{txCount}</div>
            </div>
          </div>

          <div className='cbc-modal-body'>
            {transactions.length === 0 ? (
              <div className='cbc-empty'>No transaction details available.</div>
            ) : (
              transactions.map((tx, idx) => {
                const op = tx.operationType || {};
                const td = tx.transactionDetails || {};
                const products = tx.products || [];
                const isReturned = td.deliveryStatus === "returned";
                return (
                  <div className='cbc-tx-card' key={tx.transID || tx._id || idx} style={isReturned ? { opacity: 0.55 } : {}}>
                    <div className='cbc-tx-card-hdr'>
                      <div>
                        <div className='cbc-tx-id'>#{(tx.transID || tx._id || "—").slice(0, 14)}…</div>
                        <div className='cbc-tx-date'>{fmtDate(td.date || tx.createdAt)}</div>
                      </div>
                      <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
                        <span className='cbc-tx-badge cbc-tx-badge-loan'>Loan</span>
                        {isReturned && <span className='cbc-tx-badge cbc-tx-badge-ret'>Returned</span>}
                        <span className='cbc-tx-sold-by'>by {(tx.soldBy || {}).fullName || "—"}</span>
                      </div>
                    </div>
                    <div className='cbc-tx-card-body'>
                      {products.length > 0 && (
                        <div>
                          <div className='cbc-items-label'>Items</div>
                          <div className='cbc-products'>
                            {products.map((p, pi) => (
                              <div className='cbc-product-chip' key={p._id || p.id || pi}>
                                <span>{p.name || p.productName || "Product"}</span>
                                {p.quantity && <span className='cbc-chip-qty'>×{p.quantity}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className='cbc-tx-row'>
                        <span className='cbc-tx-row-label'>Initial payment</span>
                        <span className='cbc-tx-row-val cbc-val-green'>
                          {currency} {fmt(op.intialPayment || 0)}
                        </span>
                      </div>
                      <div className='cbc-tx-row'>
                        <span className='cbc-tx-row-label'>Outstanding balance</span>
                        <span className='cbc-tx-row-val cbc-val-amber'>
                          {currency} {fmt(op.balance || 0)}
                        </span>
                      </div>
                      {td.totalAmount && (
                        <div className='cbc-tx-row'>
                          <span className='cbc-tx-row-label'>Total amount</span>
                          <span className='cbc-tx-row-val'>
                            {currency} {fmt(td.totalAmount)}
                          </span>
                        </div>
                      )}
                      {op.dateToPay && (
                        <div className='cbc-tx-row'>
                          <span className='cbc-tx-row-label'>Due date</span>
                          <span className='cbc-tx-row-val cbc-val-amber'>{fmtDate(op.dateToPay)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className='cbc-modal-footer'>
            <button className='cbc-modal-btn cbc-modal-btn-add' onClick={() => setConfirm("add")}>
              <IconPlus /> Add {currency} {fmt(balance)} to Transaction
            </button>
            <button className='cbc-modal-btn cbc-modal-btn-clear' onClick={() => setConfirm("clear")}>
              <IconTrash /> Clear Balance
            </button>
            <button className='cbc-modal-btn cbc-modal-btn-close' onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation dialog — stacks on top of the transaction modal */}
      {confirm && (
        <ConfirmDialog
          type={confirm}
          balance={balance}
          currency={currency}
          customerName={customerName}
          onConfirm={() => {
            if (confirm === "add") {
              onAddToTransaction(balance, transactions);
            } else {
              onClearBalance(transactions);
            }
            setConfirm(null);
            onClose();
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>,
    document.body
  );
}

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
export default function CustomerBalanceChecker({
  customerID,
  customerName = "Customer",
  currency = "₦",
  fetchBalance,
  onAddToTransaction,
  onClearBalance,
  onCancel,
}) {
  const [state, setState] = useState("idle");
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [confirm, setConfirm] = useState(null); // "add" | "clear" | null
  const [hasChecked, setHasChecked] = useState(false);

  /* Reset when customer changes */
  useEffect(() => {
    setState("idle");
    setHasChecked(false);
    setBalance(0);
    setTransactions([]);
    setShowModal(false);
    setConfirm(null);
  }, [customerID]);

  const handleCheck = async () => {
    if (hasChecked) {
      // If already checked, this button acts as a "Cancel" to reset everything
      setState("idle");
      setBalance(0);
      setTransactions([]);
      setShowModal(false);
      setConfirm(null);
      setHasChecked(false);
      onCancel?.(); // Notify parent that balance check has been cancelled/reset
      return;
    }

    if (!customerID || !fetchBalance) return;
    setState("loading");
    try {
      const result = await fetchBalance(customerID);
      const bal = result?.balance || 0;
      const txs = result?.transactions || [];
      setBalance(bal);
      setTransactions(txs);
      setState(bal > 0 ? "has-balance" : "no-balance");
      setHasChecked(true);
    } catch (err) {
      console.error("Balance fetch error:", err);
      setState("idle");
    }
  };

  const handleAddToTransaction = async () => {
    console.log("Adding to transaction:", { balance, transactions });

    onAddToTransaction?.(balance, transactions);
    setState("no-balance");
    setBalance(0);
    setTransactions([]);
    setShowModal(false);
    setConfirm(null);
  };

  const handleClearBalance = async () => {
    onClearBalance?.(transactions);
    setState("no-balance");
    setBalance(0);
    setTransactions([]);
    setShowModal(false);
    setConfirm(null);
  };

  const canCheck = !!customerID && !!fetchBalance;

  return (
    <>
      <div className='cbc-wrap'>
        <div className='cbc-header'>
          <div className='cbc-header-left'>
            <div className='cbc-icon-wrap'>
              <IconWallet />
            </div>
            <div>
              <div className='cbc-title'>Customer Balance</div>
              <div className='cbc-subtitle'>
                {state === "idle" && (hasChecked ? "Balance cleared" : "Check for outstanding balance")}
                {state === "loading" && "Checking balance\u2026"}
                {state === "no-balance" && "No outstanding balance"}
                {state === "has-balance" && "Outstanding balance found"}
              </div>
            </div>
          </div>
          <button className='cbc-check-btn' onClick={handleCheck} disabled={!canCheck || state === "loading"}>
            <IconSearch />
            {state === "loading" ? "Checking\u2026" : hasChecked ? "Cancel" : "Check Balance"}
          </button>
        </div>

        {state !== "idle" && (
          <>
            <div className='cbc-divider' />
            <div className='cbc-content'>
              {state === "loading" && (
                <div className='cbc-loading'>
                  <div className='cbc-spinner' />
                  <span className='cbc-loading-text'>Checking {customerName}&apos;s balance&hellip;</span>
                </div>
              )}

              {state === "no-balance" && (
                <div className='cbc-no-balance'>
                  <div className='cbc-check-circle'>
                    <IconCheck />
                  </div>
                  <div>
                    <div className='cbc-nb-title'>All Clear!</div>
                    <div className='cbc-nb-sub'>{customerName} has no outstanding balance.</div>
                  </div>
                </div>
              )}

              {state === "has-balance" && (
                <>
                  <div className='cbc-balance-card'>
                    <div>
                      <div className='cbc-balance-label'>Outstanding Balance</div>
                      <div
                        className='cbc-balance-amount'
                        onClick={() => setShowModal(true)}
                        role='button'
                        tabIndex={0}
                        title='Click to view transaction details'
                        onKeyDown={(e) => e.key === "Enter" && setShowModal(true)}
                      >
                        {currency} {fmt(balance)}
                      </div>
                      <div className='cbc-balance-hint'>
                        Tap amount to view {transactions.length} loan transaction{transactions.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div className='cbc-balance-badge'>Unpaid</div>
                  </div>

                  <div className='cbc-cta-row'>
                    <button className='cbc-btn cbc-btn-add' onClick={() => setConfirm("add")}>
                      <IconPlus /> Add to Transaction
                    </button>
                    <button className='cbc-btn cbc-btn-clear' onClick={() => setConfirm("clear")}>
                      <IconTrash /> Clear Balance
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Transaction detail modal */}
      {showModal && (
        <TransactionModal
          transactions={transactions}
          balance={balance}
          currency={currency}
          customerName={customerName}
          onAddToTransaction={handleAddToTransaction}
          onClearBalance={handleClearBalance}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Confirm dialog triggered from main CTAs (outside modal) */}
      {confirm && !showModal && (
        <ConfirmDialog
          type={confirm}
          balance={balance}
          currency={currency}
          customerName={customerName}
          onConfirm={() => {
            confirm === "add" ? handleAddToTransaction() : handleClearBalance();
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}
